import { useState } from "react";
import { Card, CardBody, Badge, Button, Dialog, Portal, SearchInput } from "@one-nexty/components-web";
import * as Icons from "@one-nexty/icons";
import {
  // Animées
  AnimatedMenuIcon,
  AnimatedCheckIcon,
  AnimatedThemeIcon,
  AnimatedChevronIcon,
  AnimatedEyeIcon,
  AnimatedCopyIcon,
  AnimatedHeartIcon,
  AnimatedStarIcon,
  AnimatedLockIcon,
  AnimatedBellIcon,
  AnimatedVolumeIcon,
  AnimatedUploadIcon,
  AnimatedTrashIcon,
  AnimatedBookmarkIcon,
  // Illustrations Vectorielles Animées (Scènes & Personnages)
  EmptyStateIllustration,
  CelebrationIllustration,
  ProcessingCloudIllustration,
  SecurityScanIllustration,
  ErrorRobotIllustration,
  UserOnboardingIllustration,
  AnalyticsChartIllustration,
  PaymentCheckoutIllustration,
  SystemMaintenanceIllustration,
  NotFoundIllustration,
  SearchNoResultsIllustration,
  RocketLaunchIllustration,
  SuccessCheckIllustration,
  ShieldLockIllustration,
  InboxMailIllustration,
  EmailSentIllustration,
  ChatMessageIllustration,
  NotificationCenterIllustration,
  NewsletterSubscribeIllustration,
  // Scènes à Variables (Props Interactives)
  LoadingProgressIllustration,
  FileUploadIllustration,
  PasswordStrengthIllustration,
  StorageMeterIllustration,
  TeamAvatarsIllustration,
  NetworkStatusIllustration,
  // Scènes à Variables — Batch 2
  WeatherIllustration,
  BatteryLevelIllustration,
  RatingStarsIllustration,
  EmojiMoodIllustration,
  StepProgressIllustration,
  SyncStatusIllustration,
  CartIllustration,
  PricingTierIllustration,
} from "@one-nexty/icons";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

// Grille de toutes les icônes statiques
const ALL_STATIC_ICONS = [
  // Thème & Solides (Paires)
  { name: "SunIcon", category: "Thème", Icon: Icons.SunIcon },
  { name: "SunSolidIcon", category: "Thème (Solid)", Icon: Icons.SunSolidIcon },
  { name: "MoonIcon", category: "Thème", Icon: Icons.MoonIcon },
  { name: "MoonSolidIcon", category: "Thème (Solid)", Icon: Icons.MoonSolidIcon },
  { name: "HeartIcon", category: "Favoris", Icon: Icons.HeartIcon },
  { name: "HeartSolidIcon", category: "Favoris (Solid)", Icon: Icons.HeartSolidIcon },
  { name: "StarIcon", category: "Évaluation", Icon: Icons.StarIcon },
  { name: "StarSolidIcon", category: "Évaluation (Solid)", Icon: Icons.StarSolidIcon },
  { name: "BookmarkIcon", category: "Favoris", Icon: Icons.BookmarkIcon },
  { name: "BookmarkSolidIcon", category: "Favoris (Solid)", Icon: Icons.BookmarkSolidIcon },
  { name: "BellIcon", category: "Notifications", Icon: Icons.BellIcon },
  { name: "BellSolidIcon", category: "Notifications (Solid)", Icon: Icons.BellSolidIcon },
  { name: "LockIcon", category: "Sécurité", Icon: Icons.LockIcon },
  { name: "LockOpenIcon", category: "Sécurité", Icon: Icons.LockOpenIcon },
  { name: "LockSolidIcon", category: "Sécurité (Solid)", Icon: Icons.LockSolidIcon },
  { name: "ShieldIcon", category: "Sécurité", Icon: Icons.ShieldIcon },
  { name: "ShieldSolidIcon", category: "Sécurité (Solid)", Icon: Icons.ShieldSolidIcon },
  { name: "FolderIcon", category: "Fichiers", Icon: Icons.FolderIcon },
  { name: "FolderSolidIcon", category: "Fichiers (Solid)", Icon: Icons.FolderSolidIcon },
  { name: "UserIcon", category: "Utilisateur", Icon: Icons.UserIcon },
  { name: "UserSolidIcon", category: "Utilisateur (Solid)", Icon: Icons.UserSolidIcon },
  { name: "UsersIcon", category: "Utilisateur", Icon: Icons.UsersIcon },
  { name: "UsersSolidIcon", category: "Utilisateur (Solid)", Icon: Icons.UsersSolidIcon },
  // Sélections & Inputs
  { name: "SquareIcon", category: "Sélection", Icon: Icons.SquareIcon },
  { name: "CheckSquareIcon", category: "Sélection", Icon: Icons.CheckSquareIcon },
  { name: "CheckSquareSolidIcon", category: "Sélection (Solid)", Icon: Icons.CheckSquareSolidIcon },
  { name: "CircleIcon", category: "Sélection", Icon: Icons.CircleIcon },
  { name: "CircleDotIcon", category: "Sélection", Icon: Icons.CircleDotIcon },
  // Status & Feedback
  { name: "InfoIcon", category: "Feedback", Icon: Icons.InfoIcon },
  { name: "InfoSolidIcon", category: "Feedback (Solid)", Icon: Icons.InfoSolidIcon },
  { name: "AlertTriangleIcon", category: "Feedback", Icon: Icons.AlertTriangleIcon },
  { name: "AlertTriangleSolidIcon", category: "Feedback (Solid)", Icon: Icons.AlertTriangleSolidIcon },
  { name: "CheckCircleIcon", category: "Feedback", Icon: Icons.CheckCircleIcon },
  { name: "CheckCircleSolidIcon", category: "Feedback (Solid)", Icon: Icons.CheckCircleSolidIcon },
  { name: "XCircleIcon", category: "Feedback", Icon: Icons.XCircleIcon },
  { name: "XCircleSolidIcon", category: "Feedback (Solid)", Icon: Icons.XCircleSolidIcon },
  // Actions & Navigation
  { name: "PlusIcon", category: "Actions", Icon: Icons.PlusIcon },
  { name: "MinusIcon", category: "Actions", Icon: Icons.MinusIcon },
  { name: "XIcon", category: "Actions", Icon: Icons.XIcon },
  { name: "CheckIcon", category: "Actions", Icon: Icons.CheckIcon },
  { name: "SearchIcon", category: "Actions", Icon: Icons.SearchIcon },
  { name: "ArrowRightIcon", category: "Navigation", Icon: Icons.ArrowRightIcon },
  { name: "ArrowLeftIcon", category: "Navigation", Icon: Icons.ArrowLeftIcon },
  { name: "ArrowUpIcon", category: "Navigation", Icon: Icons.ArrowUpIcon },
  { name: "ArrowDownIcon", category: "Navigation", Icon: Icons.ArrowDownIcon },
  { name: "ChevronDownIcon", category: "Navigation", Icon: Icons.ChevronDownIcon },
  { name: "ChevronUpIcon", category: "Navigation", Icon: Icons.ChevronUpIcon },
  { name: "ChevronRightIcon", category: "Navigation", Icon: Icons.ChevronRightIcon },
  { name: "ChevronLeftIcon", category: "Navigation", Icon: Icons.ChevronLeftIcon },
  { name: "RefreshIcon", category: "Actions", Icon: Icons.RefreshIcon },
  { name: "ExternalLinkIcon", category: "Navigation", Icon: Icons.ExternalLinkIcon },
  { name: "ShareIcon", category: "Actions", Icon: Icons.ShareIcon },
  { name: "FilterIcon", category: "Actions", Icon: Icons.FilterIcon },
  { name: "GridIcon", category: "Navigation", Icon: Icons.GridIcon },
  { name: "ListIcon", category: "Navigation", Icon: Icons.ListIcon },
  // Médias, Tech & Système
  { name: "VolumeIcon", category: "Média", Icon: Icons.VolumeIcon },
  { name: "VolumeMuteIcon", category: "Média", Icon: Icons.VolumeMuteIcon },
  { name: "PlayIcon", category: "Média", Icon: Icons.PlayIcon },
  { name: "PauseIcon", category: "Média", Icon: Icons.PauseIcon },
  { name: "CopyIcon", category: "Système", Icon: Icons.CopyIcon },
  { name: "FileIcon", category: "Système", Icon: Icons.FileIcon },
  { name: "TrashIcon", category: "Système", Icon: Icons.TrashIcon },
  { name: "InboxIcon", category: "Système", Icon: Icons.InboxIcon },
  { name: "UploadIcon", category: "Système", Icon: Icons.UploadIcon },
  { name: "DownloadIcon", category: "Système", Icon: Icons.DownloadIcon },
  { name: "SettingsIcon", category: "Système", Icon: Icons.SettingsIcon },
  { name: "SparklesIcon", category: "Système", Icon: Icons.SparklesIcon },
  { name: "ChartIcon", category: "Tech", Icon: Icons.ChartIcon },
  { name: "CloudIcon", category: "Tech", Icon: Icons.CloudIcon },
  { name: "ServerIcon", category: "Tech", Icon: Icons.ServerIcon },
  { name: "DatabaseIcon", category: "Tech", Icon: Icons.DatabaseIcon },
  { name: "CpuIcon", category: "Tech", Icon: Icons.CpuIcon },
  { name: "LayersIcon", category: "Tech", Icon: Icons.LayersIcon },
  { name: "GlobeIcon", category: "Tech", Icon: Icons.GlobeIcon },
  { name: "MailIcon", category: "Communication", Icon: Icons.MailIcon },
  { name: "PhoneIcon", category: "Communication", Icon: Icons.PhoneIcon },
  { name: "MapPinIcon", category: "Communication", Icon: Icons.MapPinIcon },
  { name: "ClockIcon", category: "Temps", Icon: Icons.ClockIcon },
  { name: "CalendarIcon", category: "Temps", Icon: Icons.CalendarIcon },
  { name: "ZapIcon", category: "Tech", Icon: Icons.ZapIcon },
  { name: "CodeIcon", category: "Tech", Icon: Icons.CodeIcon },
  { name: "TerminalIcon", category: "Tech", Icon: Icons.TerminalIcon },
];

export function IconsSection() {
  // États interactifs pour les icônes animées
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [isEyeVisible, setIsEyeVisible] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHeartActive, setIsHeartActive] = useState(true);
  const [isStarActive, setIsStarActive] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isRinging, setIsRinging] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [isBookmarkActive, setIsBookmarkActive] = useState(true);

  // États pour les scènes à variables
  const [loadingProgress, setLoadingProgress] = useState(60);
  const [loadingStatus, setLoadingStatus] = useState<"loading" | "processing" | "done">("loading");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [passwordStrength, setPasswordStrength] = useState<0 | 1 | 2 | 3 | 4>(2);
  const [storageUsed, setStorageUsed] = useState(65);
  const [teamCount, setTeamCount] = useState(3);
  const [networkStatus, setNetworkStatus] = useState<"connected" | "connecting" | "offline">("connected");
  // Batch 2
  const [weatherCondition, setWeatherCondition] = useState<"sunny" | "cloudy" | "rainy" | "stormy" | "snowy">("sunny");
  const [batteryLevel, setBatteryLevel] = useState(75);
  const [batteryCharging, setBatteryCharging] = useState(false);
  const [starRating, setStarRating] = useState<0 | 1 | 2 | 3 | 4 | 5>(4);
  const [starReviews] = useState(128);
  const [emojiMood, setEmojiMood] = useState<1 | 2 | 3 | 4 | 5>(4);
  const [stepCurrent, setStepCurrent] = useState(2);
  const [stepTotal, setStepTotal] = useState<3 | 4 | 5>(4);
  const [syncStatus, setSyncStatus] = useState<"synced" | "syncing" | "error" | "offline">("synced");
  const [cartCount, setCartCount] = useState(3);
  const [pricingTier, setPricingTier] = useState<"free" | "pro" | "enterprise">("pro");

  // Recherche & Filtre d'icônes
  const [search, setSearch] = useState("");

  // Inspecteur Modal
  const [selectedIcon, setSelectedIcon] = useState<{ name: string; Icon: React.ComponentType<Icons.IconProps> } | null>(null);
  const [iconSize, setIconSize] = useState<Icons.IconSize>("lg");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleCopyCode = (name: string) => {
    const jsxCode = `<${name} size="${iconSize}" />`;
    navigator.clipboard.writeText(jsxCode);
    showToast(`Code JSX copié : ${jsxCode}`);
  };

  const handleCopySvg = (name: string) => {
    const svgCode = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><!-- ${name} --></svg>`;
    navigator.clipboard.writeText(svgCode);
    showToast(`Code SVG brut copié pour ${name}`);
  };

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleCopyCard = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const triggerUpload = () => {
    setIsUploading(true);
    setTimeout(() => setIsUploading(false), 1500);
  };

  const triggerRinging = () => {
    setIsRinging(true);
    setTimeout(() => setIsRinging(false), 1500);
  };

  const filteredIcons = ALL_STATIC_ICONS.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()) || item.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Section
      id="icons"
      eyebrow="Composants"
      title="Icônes & illustrations"
      description="@one-nexty/icons — icônes UI 24×24, micro-animations et 19 illustrations vectorielles. Aucune dépendance externe."
    >
      {/* Notification éphémère. Portalisée pour la même raison que la modale :
          un `fixed` posé dans l'arbre de la section se recale sur le premier
          ancêtre transformé et remonte avec le défilement. */}
      {toastMessage && (
        <Portal>
          <div className="fixed right-6 bottom-6 z-50 transform-gpu animate-slide-up rounded-lg bg-fg px-4 py-3 text-sm font-medium text-surface-raised shadow-lg">
            {toastMessage}
          </div>
        </Portal>
      )}

      <SubSection title="Illustrations" hint="Dessins multi-couches animés">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 my-4">
          {/* Inbox Mail */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <InboxMailIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">InboxMailIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Enveloppe 3D, Lettre volante & Badge +1</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("InboxMailIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Email Sent */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <EmailSentIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">EmailSentIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Envol de l'email & Sceau vert de confirmation</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("EmailSentIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Chat Message */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <ChatMessageIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">ChatMessageIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Bulles de discussion & Frappe 3 points '...'</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("ChatMessageIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Notification Center */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <NotificationCenterIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">NotificationCenterIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Cloche oscillante & Cartes d'alerte</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("NotificationCenterIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Newsletter Subscribe */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <NewsletterSubscribeIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">NewsletterSubscribeIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Boîte aux lettres & Drapeau rouge levé</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("NewsletterSubscribeIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* User Onboarding Character */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <UserOnboardingIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">UserOnboardingIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Personnage d'accueil chaleureux & coucou</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("UserOnboardingIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Rocket Launch */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <RocketLaunchIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">RocketLaunchIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Décollage de fusée & propulsion</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("RocketLaunchIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Success Check */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <SuccessCheckIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">SuccessCheckIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Sceau de validation & Ondes vertes</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("SuccessCheckIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Analytics Chart */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <AnalyticsChartIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">AnalyticsChartIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Graphique de croissance & Badge +34%</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("AnalyticsChartIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Payment Checkout */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <PaymentCheckoutIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">PaymentCheckoutIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Carte bancaire & Pièce de monnaie</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("PaymentCheckoutIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Shield Lock */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <ShieldLockIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">ShieldLockIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Bouclier & Coffre fort verrouillé</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("ShieldLockIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Processing Cloud Illustration */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <ProcessingCloudIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">ProcessingCloudIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Nuage IA & Orbite technologique fluide</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("ProcessingCloudIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Not Found 404 Illustration */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <NotFoundIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">NotFoundIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Astronaute & Planète 404 en apesanteur</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("NotFoundIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* System Maintenance */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <SystemMaintenanceIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">SystemMaintenanceIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Engrenage & Clé à molette en action</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("SystemMaintenanceIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Search No Results */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <SearchNoResultsIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">SearchNoResultsIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Grande Loupe d'exploration & Point d'interrogation</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("SearchNoResultsIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Empty State Illustration */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <EmptyStateIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">EmptyStateIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Lévitation isométrique & éléments volants</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("EmptyStateIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Celebration Illustration */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <CelebrationIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">CelebrationIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Rayons tournants, Médaille & Confettis</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("CelebrationIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Security Scan Illustration */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <SecurityScanIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">SecurityScanIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Bouclier d'énergie & Balayage Laser</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("SecurityScanIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>

          {/* Error Robot Illustration */}
          <Card className="flex flex-col items-center justify-center p-6 text-center transition-[border-color] duration-fast ease-standard hover:border-border-strong">
            <ErrorRobotIllustration size={160} />
            <div className="mt-4">
              <h4 className="font-mono text-sm font-semibold text-fg">ErrorRobotIllustration</h4>
              <p className="text-xs text-fg-muted mt-1">Robot d'Erreur & Antenne clignotante</p>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleCopyCode("ErrorRobotIllustration")}>
                Copier JSX
              </Button>
            </div>
          </Card>
        </div>
      </SubSection>

      <SubSection title="Icônes Animées à physique de ressort (Motion for React)" hint="Cliquez sur n'importe quelle carte pour déclencher la micro-animation">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-4">
          {/* Animated Lock */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsUnlocked(!isUnlocked)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-indigo-500">
                  <AnimatedLockIcon unlocked={isUnlocked} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedLockIcon</div>
                  <div className="text-xs text-neutral-500">Serrure lumineuse & Anse pivotante</div>
                </div>
              </div>
              <Badge tone="neutral">{isUnlocked ? "unlocked: true" : "unlocked: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Bell */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={triggerRinging}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-amber-500">
                  <AnimatedBellIcon ringing={isRinging} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedBellIcon</div>
                  <div className="text-xs text-neutral-500">Oscillation & Pastille alerte</div>
                </div>
              </div>
              <Badge tone="neutral">{isRinging ? "ringing..." : "cliquer pour carillon"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Volume */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsMuted(!isMuted)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-sky-500">
                  <AnimatedVolumeIcon muted={isMuted} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedVolumeIcon</div>
                  <div className="text-xs text-neutral-500">Vagues audio ↔ Coupure</div>
                </div>
              </div>
              <Badge tone="neutral">{isMuted ? "muted: true" : "muted: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Upload */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={triggerUpload}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-teal-500">
                  <AnimatedUploadIcon uploading={isUploading} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedUploadIcon</div>
                  <div className="text-xs text-neutral-500">Envol de la flèche & Rebond</div>
                </div>
              </div>
              <Badge tone="neutral">{isUploading ? "uploading..." : "cliquer pour lancer"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Trash */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsTrashOpen(!isTrashOpen)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-rose-500">
                  <AnimatedTrashIcon open={isTrashOpen} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedTrashIcon</div>
                  <div className="text-xs text-neutral-500">Ouverture du couvercle</div>
                </div>
              </div>
              <Badge tone="neutral">{isTrashOpen ? "open: true" : "open: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Bookmark */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsBookmarkActive(!isBookmarkActive)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-blue-500">
                  <AnimatedBookmarkIcon active={isBookmarkActive} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedBookmarkIcon</div>
                  <div className="text-xs text-neutral-500">Remplissage & Enfoncement</div>
                </div>
              </div>
              <Badge tone="neutral">{isBookmarkActive ? "active: true" : "active: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Menu */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
                  <AnimatedMenuIcon isOpen={isMenuOpen} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedMenuIcon</div>
                  <div className="text-xs text-neutral-500">Morphing Hamburger ↔ Croix X</div>
                </div>
              </div>
              <Badge tone="neutral">{isMenuOpen ? "isOpen: true" : "isOpen: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Check */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsChecked(!isChecked)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-emerald-600 dark:text-emerald-400">
                  <AnimatedCheckIcon checked={isChecked} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedCheckIcon</div>
                  <div className="text-xs text-neutral-500">Tracé vectoriel continu</div>
                </div>
              </div>
              <Badge tone="neutral">{isChecked ? "checked: true" : "checked: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Theme */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800">
                  <AnimatedThemeIcon theme={theme} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedThemeIcon</div>
                  <div className="text-xs text-neutral-500">Soleil ↔ Croissant éclipsé</div>
                </div>
              </div>
              <Badge tone="neutral">{`theme: "${theme}"`}</Badge>
            </CardBody>
          </Card>

          {/* Animated Heart */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsHeartActive(!isHeartActive)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-rose-500">
                  <AnimatedHeartIcon active={isHeartActive} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedHeartIcon</div>
                  <div className="text-xs text-neutral-500">Onde de choc & Remplissage</div>
                </div>
              </div>
              <Badge tone="neutral">{isHeartActive ? "active: true" : "active: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Star */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsStarActive(!isStarActive)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-amber-400">
                  <AnimatedStarIcon active={isStarActive} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedStarIcon</div>
                  <div className="text-xs text-neutral-500">Rotation 72° & Remplissage</div>
                </div>
              </div>
              <Badge tone="neutral">{isStarActive ? "active: true" : "active: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Eye */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsEyeVisible(!isEyeVisible)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
                  <AnimatedEyeIcon visible={isEyeVisible} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedEyeIcon</div>
                  <div className="text-xs text-neutral-500">Tracé de masquage</div>
                </div>
              </div>
              <Badge tone="neutral">{isEyeVisible ? "visible: true" : "visible: false"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Copy */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={handleCopyCard}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
                  <AnimatedCopyIcon copied={isCopied} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedCopyIcon</div>
                  <div className="text-xs text-neutral-500">Pop & Micro-particules</div>
                </div>
              </div>
              <Badge tone="neutral">{isCopied ? "copied: true" : "cliquer"}</Badge>
            </CardBody>
          </Card>

          {/* Animated Chevron */}
          <Card
            className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <CardBody className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
                  <AnimatedChevronIcon expanded={isExpanded} size="lg" />
                </div>
                <div>
                  <div className="font-mono text-sm font-semibold">AnimatedChevronIcon</div>
                  <div className="text-xs text-neutral-500">Rotation 180° à ressort</div>
                </div>
              </div>
              <Badge tone="neutral">{isExpanded ? "expanded: true" : "expanded: false"}</Badge>
            </CardBody>
          </Card>
        </div>
      </SubSection>

      {/* MODALE D'INSPECTION & COPIE CODE / SVG
          Écrite avec `Dialog` plutôt qu'à la main : le `fixed inset-0` maison
          était pris au piège du premier ancêtre transformé rencontré — il se
          calait sur la carte au lieu de la fenêtre, et défilait avec elle.
          `Dialog` est portalisé dans <body> et devient un vrai voile plein
          écran, avec en prime la touche Échap, le piège à focus et le
          BottomSheet swipable sur smartphone. */}
      {selectedIcon && (
        <Dialog
          open
          onClose={() => setSelectedIcon(null)}
          title={<span className="font-mono">{selectedIcon.name}</span>}
          description="Inspecteur & générateur de code"
          footer={
            <>
              <Button variant="outline" onClick={() => handleCopySvg(selectedIcon.name)}>
                Copier le SVG brut
              </Button>
              <Button onClick={() => handleCopyCode(selectedIcon.name)}>
                Copier le JSX
              </Button>
            </>
          }
        >
          <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-surface-sunken p-8">
            <selectedIcon.Icon size={iconSize} className="text-fg" />
            <div className="flex flex-wrap items-center justify-center gap-2">
              {(["xs", "sm", "md", "lg", "xl"] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={iconSize === s ? "primary" : "outline"}
                  onClick={() => setIconSize(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>

          <p className="mt-4 text-center font-mono text-xs text-fg-muted">
            {`<${selectedIcon.name} size="${iconSize}" />`}
          </p>
        </Dialog>
      )}

      <SubSection title={`Collection Statique (${ALL_STATIC_ICONS.length} icônes)`} hint="Cliquer sur une icône pour l'inspecter et copier son code JSX / SVG">
        <div className="mb-4 max-w-md">
          <SearchInput
            placeholder="Rechercher une icône (Solid, Sun, Heart, Lock…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
          />
        </div>

        <Preview layout="stack">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 w-full">
            {filteredIcons.map(({ name, category, Icon: IconComponent }) => (
              <button
                key={name}
                type="button"
                onClick={() => setSelectedIcon({ name, Icon: IconComponent })}
                className="flex flex-col items-center justify-center p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-[border-color] duration-fast ease-standard hover:border-border-strong gap-2 text-left group"
              >
                <IconComponent size="lg" className="text-neutral-800 dark:text-neutral-200 group-hover:scale-110 transition-transform" />
                <span className="text-[11px] font-mono font-medium text-neutral-800 dark:text-neutral-200 truncate max-w-full">{name}</span>
                <span className="text-[9px] font-sans text-neutral-400 truncate max-w-full">{category}</span>
              </button>
            ))}
          </div>
        </Preview>
      </SubSection>

      {/* SUBSECTION: SCÈNES À VARIABLES */}
      <SubSection title="Scènes à Variables (Props Interactives)" hint="Illustrations qui changent d'apparence selon les props — idéal pour les états de loading, upload, onboarding...">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 my-4">

          {/* Loading Progress */}
          <Card className="flex flex-col items-center p-6 gap-4">
            <LoadingProgressIllustration size={140} progress={loadingProgress} status={loadingStatus} label={loadingStatus === "done" ? "Terminé !" : "Chargement en cours"} />
            <div className="w-full">
              <h4 className="font-mono text-sm font-semibold text-fg text-center">LoadingProgressIllustration</h4>
              <p className="text-xs text-fg-muted mt-1 text-center">Spinner ↔ Coche, Barre de progression</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-fg-muted block mb-1">progress: {loadingProgress}%</label>
                  <input type="range" min="0" max="100" value={loadingProgress} onChange={(e) => setLoadingProgress(Number(e.target.value))} className="w-full accent-indigo-500" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {(["loading", "processing", "done"] as const).map((s) => (
                    <Button key={s} size="sm" variant={loadingStatus === s ? "primary" : "outline"} onClick={() => setLoadingStatus(s)}>{s}</Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* File Upload */}
          <Card className="flex flex-col items-center p-6 gap-4">
            <FileUploadIllustration size={140} progress={uploadProgress} status={uploadStatus} fileName="rapport_annuel.pdf" />
            <div className="w-full">
              <h4 className="font-mono text-sm font-semibold text-fg text-center">FileUploadIllustration</h4>
              <p className="text-xs text-fg-muted mt-1 text-center">Icône fichier animée selon l'état</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-fg-muted block mb-1">progress: {uploadProgress}%</label>
                  <input type="range" min="0" max="100" value={uploadProgress} onChange={(e) => setUploadProgress(Number(e.target.value))} className="w-full accent-blue-500" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {(["idle", "uploading", "done", "error"] as const).map((s) => (
                    <Button key={s} size="sm" variant={uploadStatus === s ? "primary" : "outline"} onClick={() => setUploadStatus(s)}>{s}</Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Password Strength */}
          <Card className="flex flex-col items-center p-6 gap-4">
            <PasswordStrengthIllustration size={140} strength={passwordStrength} />
            <div className="w-full">
              <h4 className="font-mono text-sm font-semibold text-fg text-center">PasswordStrengthIllustration</h4>
              <p className="text-xs text-fg-muted mt-1 text-center">Cadenas coloré + 4 barres de force</p>
              <div className="mt-4">
                <label className="text-xs text-fg-muted block mb-2">strength: {passwordStrength} / 4</label>
                <div className="flex gap-2">
                  {([0, 1, 2, 3, 4] as const).map((s) => (
                    <Button key={s} size="sm" variant={passwordStrength === s ? "primary" : "outline"} onClick={() => setPasswordStrength(s)}>{s}</Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Storage Meter */}
          <Card className="flex flex-col items-center p-6 gap-4">
            <StorageMeterIllustration size={140} used={storageUsed} label={`${(storageUsed * 0.1).toFixed(1)} Go / 10 Go`} />
            <div className="w-full">
              <h4 className="font-mono text-sm font-semibold text-fg text-center">StorageMeterIllustration</h4>
              <p className="text-xs text-fg-muted mt-1 text-center">Jauge en arc, couleur adaptative</p>
              <div className="mt-4">
                <label className="text-xs text-fg-muted block mb-1">used: {storageUsed}%</label>
                <input type="range" min="0" max="100" value={storageUsed} onChange={(e) => setStorageUsed(Number(e.target.value))} className="w-full accent-violet-500" />
              </div>
            </div>
          </Card>

          {/* Team Avatars */}
          <Card className="flex flex-col items-center p-6 gap-4">
            <TeamAvatarsIllustration size={140} count={teamCount} />
            <div className="w-full">
              <h4 className="font-mono text-sm font-semibold text-fg text-center">TeamAvatarsIllustration</h4>
              <p className="text-xs text-fg-muted mt-1 text-center">Avatars empilés flottants (1 à 6)</p>
              <div className="mt-4">
                <label className="text-xs text-fg-muted block mb-2">count: {teamCount} membre{teamCount > 1 ? "s" : ""}</label>
                <div className="flex gap-2 flex-wrap">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <Button key={n} size="sm" variant={teamCount === n ? "primary" : "outline"} onClick={() => setTeamCount(n)}>{n}</Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Network Status */}
          <Card className="flex flex-col items-center p-6 gap-4">
            <NetworkStatusIllustration size={140} status={networkStatus} />
            <div className="w-full">
              <h4 className="font-mono text-sm font-semibold text-fg text-center">NetworkStatusIllustration</h4>
              <p className="text-xs text-fg-muted mt-1 text-center">WiFi animé — connecté / connexion / hors ligne</p>
              <div className="mt-4">
                <div className="flex gap-2 flex-wrap">
                  {(["connected", "connecting", "offline"] as const).map((s) => (
                    <Button key={s} size="sm" variant={networkStatus === s ? "primary" : "outline"} onClick={() => setNetworkStatus(s)}>{s}</Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

        </div>
      </SubSection>

      {/* SUBSECTION: SCÈNES à VARIABLES — BATCH 2 */}
      <SubSection title="Scènes à Variables — Batch 2" hint="Météo, batterie, étoiles, humeur, stepper, sync, panier, pricing">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 my-4">

          {/* Weather */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <WeatherIllustration size={130} condition={weatherCondition} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">WeatherIllustration</h4>
            <div className="flex gap-1 flex-wrap justify-center">
              {(["sunny", "cloudy", "rainy", "stormy", "snowy"] as const).map((c) => (
                <Button key={c} size="sm" variant={weatherCondition === c ? "primary" : "outline"} onClick={() => setWeatherCondition(c)}>
                  {c === "sunny" ? "☀️" : c === "cloudy" ? "☁️" : c === "rainy" ? "🌧" : c === "stormy" ? "⛈" : "❄️"}
                </Button>
              ))}
            </div>
          </Card>

          {/* Battery */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <BatteryLevelIllustration size={130} level={batteryLevel} charging={batteryCharging} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">BatteryLevelIllustration</h4>
            <div className="w-full space-y-2">
              <label className="text-xs text-fg-muted block">level: {batteryLevel}%</label>
              <input type="range" min="0" max="100" value={batteryLevel} onChange={(e) => setBatteryLevel(Number(e.target.value))} className="w-full accent-emerald-500" />
              <Button size="sm" variant={batteryCharging ? "primary" : "outline"} onClick={() => setBatteryCharging(!batteryCharging)} className="w-full">
                {batteryCharging ? "⚡ En charge" : "Brancher"}
              </Button>
            </div>
          </Card>

          {/* Rating Stars */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <RatingStarsIllustration size={130} rating={starRating} reviewCount={starReviews} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">RatingStarsIllustration</h4>
            <div className="flex gap-1">
              {([0, 1, 2, 3, 4, 5] as const).map((r) => (
                <Button key={r} size="sm" variant={starRating === r ? "primary" : "outline"} onClick={() => setStarRating(r)}>{r}★</Button>
              ))}
            </div>
          </Card>

          {/* Emoji Mood */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <EmojiMoodIllustration size={130} mood={emojiMood} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">EmojiMoodIllustration</h4>
            <div className="flex gap-1">
              {([1, 2, 3, 4, 5] as const).map((m) => (
                <Button key={m} size="sm" variant={emojiMood === m ? "primary" : "outline"} onClick={() => setEmojiMood(m)}>{m}</Button>
              ))}
            </div>
          </Card>

          {/* Step Progress */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <StepProgressIllustration size={130} step={stepCurrent} total={stepTotal} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">StepProgressIllustration</h4>
            <div className="w-full space-y-2">
              <div className="flex gap-1">
                <span className="text-xs text-fg-muted">Étape:</span>
                {Array.from({ length: stepTotal }, (_, i) => i + 1).map((s) => (
                  <Button key={s} size="sm" variant={stepCurrent === s ? "primary" : "outline"} onClick={() => setStepCurrent(s)}>{s}</Button>
                ))}
              </div>
              <div className="flex gap-1">
                <span className="text-xs text-fg-muted">Total:</span>
                {([3, 4, 5] as const).map((t) => (
                  <Button key={t} size="sm" variant={stepTotal === t ? "primary" : "outline"} onClick={() => { setStepTotal(t); if (stepCurrent > t) setStepCurrent(t); }}>{t}</Button>
                ))}
              </div>
            </div>
          </Card>

          {/* Sync Status */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <SyncStatusIllustration size={130} status={syncStatus} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">SyncStatusIllustration</h4>
            <div className="flex gap-1 flex-wrap justify-center">
              {(["synced", "syncing", "error", "offline"] as const).map((s) => (
                <Button key={s} size="sm" variant={syncStatus === s ? "primary" : "outline"} onClick={() => setSyncStatus(s)}>{s}</Button>
              ))}
            </div>
          </Card>

          {/* Cart */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <CartIllustration size={130} itemCount={cartCount} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">CartIllustration</h4>
            <div className="w-full space-y-1">
              <label className="text-xs text-fg-muted block">itemCount: {cartCount}</label>
              <input type="range" min="0" max="10" value={cartCount} onChange={(e) => setCartCount(Number(e.target.value))} className="w-full accent-indigo-500" />
            </div>
          </Card>

          {/* Pricing Tier */}
          <Card className="flex flex-col items-center p-5 gap-3">
            <PricingTierIllustration size={130} tier={pricingTier} />
            <h4 className="font-mono text-xs font-semibold text-fg text-center">PricingTierIllustration</h4>
            <div className="flex gap-1">
              {(["free", "pro", "enterprise"] as const).map((t) => (
                <Button key={t} size="sm" variant={pricingTier === t ? "primary" : "outline"} onClick={() => setPricingTier(t)}>{t}</Button>
              ))}
            </div>
          </Card>

        </div>
      </SubSection>
    </Section>
  );
}
